import {observer} from 'mobx-react-lite';
import {useNavigate, useParams} from 'react-router-dom';
import React, {useEffect, useState} from 'react';
import {useService} from '../../../infrastructure/service/service';
import {Project} from '../../../entity/model/project';
import {Title} from '../../../entity/model/title';
import LoadingComponent from '../components/widget/LoadingComponent';
import {PageOption} from '../../../entity/enums/page-option';
import Box from '@mui/material/Box';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {router} from '../../router/Routes';
import {Button, Card, CardContent, CircularProgress, FormControl, InputLabel, Menu, MenuItem, Select, SelectChangeEvent, TextField, Typography} from '@mui/material';
import Grid from '@mui/material/Grid2';
import Skeleton from 'react-loading-skeleton';
import WorkIcon from '@mui/icons-material/Work';
import {getStatus, OrderStatus} from '../../../entity/enums/order-status';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import DownloadIcon from '@mui/icons-material/Download';
import FileOpenIcon from '@mui/icons-material/FileOpen';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import {DragContainer} from '../components/widget/DragContainer';
import {rectSortingStrategy} from '@dnd-kit/sortable';
import {UserPermission} from '../../../entity/enums/user-permission';
import TitleDialog from '../components/title/TitleDialog';
import AuditLogsDialog from '../components/title/AuditLogsDialog';
import {getSearchType, SearchType} from '../../../entity/enums/search-type';
import RefreshIcon from '@mui/icons-material/Refresh';
import {KeyValueResource} from '../../../entity/resource/key-value-resource';
import {County} from '../../../entity/model/county';
import Link from '@mui/material/Link';
import {CountyLink} from '../../../entity/resource/county-link-resource';
import {User} from '../../../entity/model/user';
import {DropdownOption} from '../../../entity/core/dropdown-option';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AppsOutageIcon from '@mui/icons-material/AppsOutage';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CommentWidget from '../components/comment/CommentWidget';
import LaunchIcon from '@mui/icons-material/Launch';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EditIcon from '@mui/icons-material/Edit';
import {CheckCircle} from '@mui/icons-material';
import moment from 'moment';
import {State} from '../../../entity/model/state';
import {ProjectDto} from '../../../entity/dto/project-dto';
import {ParcelInfoDto} from '../../../entity/dto/parcel-info-dto';
import SummarizeIcon from '@mui/icons-material/Summarize';
import ListAltIcon from '@mui/icons-material/ListAlt';
import HistoryIcon from '@mui/icons-material/History';

import '../../../assets/css/style.css';
import '../../../assets/css/pages/title-details.css';

const TitleDetails = (): JSX.Element => {
  const {titleService, projectService, countyService, stateService, userService, organizationService, countyLinkService, titleSummaryRequestService, invoiceRequestService, lineListRequestService} = useService();
  const navigate = useNavigate();
  const {organizationId, titleId} = useParams();

  const [loading, setLoading] = useState<boolean>(false);
  const [refresh, setRefresh] = useState<boolean>(false);
  const [project, setProject] = useState<Project>();
  const [title, setTitle] = useState<Title>();
  const [orderDetails, setOrderDetails] = useState<KeyValueResource[]>([]);
  const [assignDetails, setAssignDetails] = useState<KeyValueResource[]>([]);
  const [submitPermission, setSubmitPermission] = useState<boolean>(false);
  const [reviewPermission, setReviewPermission] = useState<boolean>(false);
  const [approvePermission, setApprovePermission] = useState<boolean>(false);
  const [openTitleDialog, setOpenTitleDialog] = useState<boolean>(false);
  const [orderCounty, setOrderCounty] = useState<County>();
  const [countyLinks, setCountyLinks] = useState<CountyLink[]>([]);
  const [currentUser, setCurrentUser] = useState<User>();
  const [isExpanded, setExpanded] = useState<boolean>(false);
  const [parcelNumberOptions, setParcelNumberOptions] = useState<DropdownOption[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [actionList, setActionList] = useState<PageOption[]>([]);
  const [userOptions, setUserOptions] = useState<DropdownOption[]>([]);
  const [countyLinkEl, setCountyLinkEl] = useState<null | HTMLElement>(null);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [searchTypeOptions, setSearchTypeOptions] = useState<DropdownOption[]>([]);
  const [stateOptions, setStateOptions] = useState<DropdownOption[]>([]);
  const [countyOptions, setCountyOptions] = useState<DropdownOption[]>([]);
  const [counties, setCounties] = useState<County[]>([]);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [sameOrganization, setSameOrganization] = useState<boolean>(false);
  const [updatePermission, setUpdatePermission] = useState<boolean>(false);
  const [openAuditLogsDialog, setOpenAuditLogsDialog] = useState<boolean>(false);

  const [searchType, setSearchType] = useState<string>();
  const [dueDate, setDueDate] = useState<Date>(new Date());
  const [state, setState] = useState<string>();
  const [county, setCounty] = useState<string>();
  const [abstractor, setAbstractor] = useState<string>();
  const [reviewer, setReviewer] = useState<string>();
  const [approver, setApprover] = useState<string>();
  const [titles, setTitles] = useState<Title[]>([]);

  useEffect(() => {
    titleService.setCache(false);
    titleService.setPageOption(PageOption.DOCUMENTS);
  }, []);

  useEffect(() => {
    const broadcastChannel = new BroadcastChannel('title-updates');

    const handleMessage = async (event: MessageEvent): Promise<void> => {
      if (event.data.type === 'title-document-updated' && event.data.titleId === titleId) {
        titleService.setCache(false);
        titleService.setRefreshTitleDetails(true);
      }
    };

    broadcastChannel.addEventListener('message', handleMessage);

    return (): void => {
      broadcastChannel.removeEventListener('message', handleMessage);
      broadcastChannel.close();
    };
  }, [titleId]);

  useEffect(() => {
    const init = async (): Promise<void> => {
      setLoading(true);

      const user = await userService.getCurrentUser();
      setCurrentUser(user);

      const title = await titleService.getTitleById(organizationId ?? '', titleId ?? '');
      setTitle(title);

      if (!title) {
        navigate('/dashboard');
        return;
      }

      const project = await projectService.getProjectById(organizationId ?? '', title?.projectID ?? '');
      if (!project) {
        navigate('/dashboard');
        return;
      }

      setProject(project);
      setUpdatePermission(projectService.isPermittedTitle(title.id) && userService.isPermitted(UserPermission.UPDATE_PROJECT));

      if(user && user.organizationID !== project.createdByOrganizationID && user.organizationID !== project.assignedToOrganizationID)
        await router.navigate('/dashboard');

      const county = await countyService.getCountyById(title.countyID);
      setOrderCounty(county);

      const state = await stateService.getStateById(title.stateID);
      const abstractor = await userService.getUserById(title.assignedToUserID);
      const reviewer = await userService.getUserById(title.assignedToReviewerUserID);
      const approver = await userService.getUserById(title.assignedToApproverUserID);
      const abstractionOrganization = await organizationService.getOrganizationById(title.assignedToOrganizationID);
      const createdOrganization = await organizationService.getOrganizationById(title.createdByOrganizationID);
      const isMultiOrg = title.createdByOrganizationID !== title.assignedToOrganizationID;

      setOrderDetails([
        { label: 'Companies', value: `${createdOrganization?.name ?? ''} ${isMultiOrg ? `| ${abstractionOrganization?.name ?? ''}` : ''}` },
        { label: 'Search Type', value: getSearchType(project.searchType) },
        { label: 'Due Date', value: new Date(project.dueDate).toLocaleDateString() },
        { label: 'State', value: state?.shortName },
        { label: 'County', value: county?.name }
      ]);

      setAssignDetails([
        { label: 'Abstractor', value: abstractor ? `${abstractor.firstName} ${abstractor.lastName}` : '-' },
        { label: 'Reviewer', value: reviewer ? `${reviewer.firstName} ${reviewer.lastName}` : '-' },
        { label: 'Approver', value: approver ? `${approver.firstName} ${approver.lastName}` : '-' }
      ]);

      const searchTypes = Object.keys(SearchType).map((key: string) => ({key: key, text: SearchType[key as keyof typeof SearchType], value: key}));
      setSearchTypeOptions(searchTypes);

      const states = await stateService.getStates();
      const stateOptions = states.map((state: State) => ({key: state.id, text: state.shortName, value: state.id}));
      setStateOptions(stateOptions);

      const counties = await countyService.getCounties();
      setCounties(counties);

      const countyOptions = counties.filter((county: County) => county.stateID === title.stateID).sort((a: County, b: County) => a.name.localeCompare(b.name)).map((county: County) => ({key: county.id, text: county.name, value: county.id}));
      setCountyOptions(countyOptions);

      const users = await userService.getAllUsers(user?.organizationID ?? '', false);
      const userOptions = users.map((user: User): DropdownOption => ({key: user.id, text: userService.getDisplayName(false, user.id), value: user.id}));
      setUserOptions(userOptions);

      setSameOrganization(user?.organizationID === title.assignedToOrganizationID);
      setSearchType(searchTypes.find((option: DropdownOption) => option.key === project.searchType)?.value ?? '');
      setDueDate(new Date(project.dueDate));
      setState(stateOptions.find((option: DropdownOption) => option.key === title.stateID)?.value ?? '');
      setCounty(countyOptions.find((option: DropdownOption) => option.key === title.countyID)?.value ?? '');
      setAbstractor(userOptions.find((option: DropdownOption) => option.key === title.assignedToUserID)?.value ?? '');
      setReviewer(userOptions.find((option: DropdownOption) => option.key === title.assignedToReviewerUserID)?.value ?? '');
      setApprover(userOptions.find((option: DropdownOption) => option.key === title.assignedToApproverUserID)?.value ?? '');

      setSubmitPermission(title.status === getStatus(OrderStatus.OPEN) && user !== undefined && title.assignedToUserID === user.id);
      setReviewPermission(title.status === getStatus(OrderStatus.PENDING_REVIEW) && user !== undefined && title.assignedToReviewerUserID === user.id);
      setApprovePermission(title.status === getStatus(OrderStatus.PENDING_APPROVAL) && user !== undefined && title.assignedToApproverUserID === user.id);

      const countyLinkResource = await countyLinkService.getCountyLinks(title.countyID);
      if(countyLinkResource)
        setCountyLinks(countyLinkResource.links.filter((countyLink: CountyLink): boolean => countyLink.active));
      else
        setCountyLinks([]);

      const titles = await titleService.getTitles(project.createdByOrganizationID, project.id);
      setTitles(titles);

      setParcelNumberOptions(titles.flatMap((title: Title): DropdownOption[] => title.tmsParcelNumbers.map((tms: string): DropdownOption => ({key: title.id, value: title.id, text: tms}))));
      setActionList(titleService.getPageOptions());

      await invoiceRequestService.loadInvoiceTemplates(project.createdByOrganizationID, title.id);
      await titleSummaryRequestService.loadTitleSummaryRequests(project.createdByOrganizationID, title.id);
      await lineListRequestService.loadLineListRequests(project.createdByOrganizationID, title.id);

      titleService.setRefreshTitleDetails(false);
      setLoading(false);
    }

    if ((!titleService.getCache() && !titleService.isLoading()) || titleService.isRefreshTitleDetails())
      init();
  }, [titleService.getCache(), titleService.isRefreshTitleDetails()]);

  const isDisabled = (pageOption: PageOption): boolean=> {
    if (!title)
      return true;

    if (pageOption === PageOption.EXPORT)
      return !userService.isPermitted(UserPermission.CREATE_ORG_TITLE_TEMPLATE);

    if (pageOption === PageOption.VALIDATE_DOCUMENTS || pageOption === PageOption.IMPORT || pageOption === PageOption.DOCUMENT_RUNNER || pageOption === PageOption.INVOICES || pageOption === PageOption.LINE_LIST_REPORTS || pageOption === PageOption.REPORTS)
      return !projectService.isPermittedTitle(title.id);

    return false;
  }

  const handlePageOptionClick = (pageOption: PageOption): void => {
    if (pageOption === PageOption.AUDIT_LOGS)
      setOpenAuditLogsDialog(true);
    else
      titleService.setPageOption(pageOption);
    setAnchorEl(null);
  }

  const refreshPage = async (): Promise<void> => {
    setRefresh(true);

    await titleService.getTitleById(project?.createdByOrganizationID ?? '', title?.id ?? '');

    setRefresh(false);
  }

  const handleExpandDetails = (): void => {
    setExpanded((prevState: boolean): boolean => !prevState);
  }

  const renderCountyLinks = (): JSX.Element => {
    return (
      <Box display='flex' flexDirection={'column'} minHeight={240} maxHeight={300} width={300} overflow={'auto'} justifyContent={'flex-start'} gap={2} sx={{ zIndex: 0 }}>
        {
          countyLinks.map((countyLink: CountyLink, index: number): JSX.Element =>
            <Link
              key={index}
              id={`link-county-${index}`}
              className={'county-link'}
              href={countyLink.url}
              target='_blank'
              rel='noopener noreferrer'
              underline='hover'>
              {countyLink.title}
              <LaunchIcon className={'county-link-icon'}/>
            </Link>
          )
        }
      </Box>
    );
  }

  const handleEditOrder = (): void => {
    if (!updatePermission)
      return;

    setEditMode(true);
    setExpanded(true);
  }

  const handleSearchTypeChange = (event: SelectChangeEvent): void => {
    setSearchType(event.target.value);
  }

  const handleDueDateChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setDueDate(new Date(event.target.value));
  }

  const handleStateChange = (event: SelectChangeEvent): void => {
    let stateCounties = counties.filter((county: County) => county.stateID === event.target.value).sort((a: County, b: County) => a.name.localeCompare(b.name));
    setCountyOptions(stateCounties.map((county: County) => ({key: county.id, text: county.name, value: county.id})));

    setState(event.target.value);
  }

  const handleCountyChange = (event: SelectChangeEvent): void => {
    setCounty(event.target.value);
  }

  const handleAbstractChange = (event: SelectChangeEvent): void => {
    setAbstractor(event.target.value);
  }

  const handleReviewerChange = (event: SelectChangeEvent): void => {
    setReviewer(event.target.value);
  }

  const handleApproverChange = (event: SelectChangeEvent): void => {
    setApprover(event.target.value);
  }

  const handleParcelNumberChange = async (event: SelectChangeEvent): Promise<void> => {
    await router.navigate(`/organization/${organizationId}/title/${event.target.value}`);
    titleService.setRefreshTitleDetails(true);
  }

  const handleSubmitOrder = async (): Promise<void> => {
    if (!project)
      return;

    setSubmitLoading(true);

    const projectTitles = [];
    for (const projectTitle of titles) {
      const parcelEntry: ParcelInfoDto = {...projectTitle};

      if (projectTitle.id === title?.id) {
        parcelEntry.assignedToReviewerUserID = currentUser?.organizationID === projectTitle.assignedToOrganizationID ? reviewer ?? '' : projectTitle.assignedToReviewerUserID;
        parcelEntry.assignedToApproverUserID = currentUser?.organizationID === projectTitle.createdByOrganizationID ? approver ?? '' : projectTitle.assignedToApproverUserID;
        parcelEntry.assignedToUserID = currentUser?.organizationID === projectTitle.assignedToOrganizationID ? abstractor ?? '' : projectTitle.assignedToUserID;
        parcelEntry.stateID = state ?? projectTitle.stateID;
        parcelEntry.countyID = county ?? projectTitle.countyID;
      }

      projectTitles.push(parcelEntry);
    }

    const payload: ProjectDto = {
      ...project,
      dueDate: moment(dueDate).format('YYYY-MM-DD'),
      searchType: searchType ?? project.searchType,
      titleSectionTemplateID: project.titleSectionTemplateID,
      projectTitles: projectTitles
    };

    await projectService.updateProject(project.createdByOrganizationID, project.id, payload);

    setSubmitLoading(false);
    setEditMode(false);
    titleService.setRefreshTitleDetails(true);
  }

  const renderTemplate = (isAssignDetails: boolean, index: number, value?: string): JSX.Element => {
    if (editMode) {
      if (index === 0 && sameOrganization && isAssignDetails)
        return (
          <FormControl className={'card-input'}>
            <InputLabel>Abstractor</InputLabel>
            <Select
              id={'input-abstractor'}
              name='abstractor'
              value={abstractor ?? userOptions.find((option: DropdownOption): boolean => option.key === title?.assignedToUserID)?.value ?? ''}
              label='Abstractor'
              onChange={handleAbstractChange}>
              {
                userOptions.map((option: DropdownOption): JSX.Element => <MenuItem key={option.key} value={option.value}>{option.text}</MenuItem>)
              }
            </Select>
          </FormControl>
        );

      if (index === 1) {
        if (isAssignDetails && sameOrganization)
          return (
            <FormControl className={'card-input'}>
              <InputLabel>Reviewer</InputLabel>
              <Select
                id={'input-reviewer'}
                name='reviewer'
                value={reviewer ?? userOptions.find((option: DropdownOption): boolean => option.key === title?.assignedToReviewerUserID)?.value ?? ''}
                label='Reviewer'
                onChange={handleReviewerChange}>
                {
                  userOptions.map((option: DropdownOption): JSX.Element => <MenuItem key={option.key} value={option.value}>{option.text}</MenuItem>)
                }
              </Select>
            </FormControl>
          );

        if (!isAssignDetails)
          return (
            <FormControl className={'card-input'}>
              <InputLabel>Search Type</InputLabel>
              <Select
                id={'input-search-type'}
                name='searchType'
                value={searchType ?? searchTypeOptions.find((option: DropdownOption) => option.key === project?.searchType)?.value ?? ''}
                label='Search Type'
                onChange={handleSearchTypeChange}>
                {
                  searchTypeOptions.map((option: DropdownOption): JSX.Element => <MenuItem key={option.key} value={option.value}>{option.text}</MenuItem>)
                }
              </Select>
            </FormControl>
          );
      }

      if (index === 2) {
        if (isAssignDetails && currentUser?.organizationID === title?.createdByOrganizationID)
          return (
            <FormControl className={'card-input'}>
              <InputLabel>Approver</InputLabel>
              <Select
                id={'input-approver'}
                name='approver'
                value={approver ?? userOptions.find((option: DropdownOption): boolean => option.key === title?.assignedToApproverUserID)?.value ?? ''}
                label='Approver'
                onChange={handleApproverChange}>
                {
                  userOptions.map((option: DropdownOption): JSX.Element => <MenuItem key={option.key} value={option.value}>{option.text}</MenuItem>)
                }
              </Select>
            </FormControl>
          );

        if (!isAssignDetails)
          return (
            <TextField
              id={'input-due-date'}
              className={'card-input'}
              type={'date'}
              label='Due Date'
              name='dueDate'
              value={moment(dueDate).format('YYYY-MM-DD')}
              onChange={handleDueDateChange}/>
          );
      }

      if (index === 3)
        return (
          <FormControl className={'card-input'}>
            <InputLabel>State</InputLabel>
            <Select
              id={'input-state'}
              name='stateID'
              value={state ?? stateOptions.find((option: DropdownOption): boolean => option.key === title?.stateID)?.value ?? ''}
              label='State'
              onChange={handleStateChange}>
              {
                stateOptions.map((option: DropdownOption): JSX.Element => <MenuItem key={option.key} value={option.value}>{option.text}</MenuItem>)
              }
            </Select>
          </FormControl>
        );

      if (index === 4)
        return (
          <FormControl className={'card-input'}>
            <InputLabel>County</InputLabel>
            <Select
              id={'input-county'}
              name='countyID'
              value={county ?? countyOptions.find((option: DropdownOption): boolean => option.key === title?.countyID)?.value ?? ''}
              label='County'
              onChange={handleCountyChange}>
              {
                countyOptions.map((option: DropdownOption): JSX.Element => <MenuItem key={option.key} value={option.value}>{option.text}</MenuItem>)
              }
            </Select>
          </FormControl>
        );
    }

    return <Typography className={`details-card-item-value ${isAssignDetails ? 'link' : ''}`}>{value ?? <Skeleton />}</Typography>;
  }

  return (
    <>
      {
        !loading && project
          ? <>
              {
                refresh &&
                  <LoadingComponent/>
              }
              <Box display={'flex'}>
                <Box display={'flex'}>
                  <ArrowBackIcon id={'btn-back-to-dashboard'} className={'breadcrumb-icon'} onClick={(): Promise<void> => router.navigate(project?.status === getStatus(OrderStatus.COMPLETED) ? '/completed-files' : '/dashboard')}/>
                  <Typography id={'text-project-order-number'} className={'breadcrumb-title'}>{ `File Name #${project.orderNumber}` }</Typography>
                  <Typography id={'btn-expand-details'} className={'breadcrumb-header-expand-text'} onClick={handleExpandDetails}>
                    {
                      isExpanded
                        ? 'Hide Details'
                        : 'Show Details'
                    }
                  </Typography>
                </Box>
                <Box className={'breadcrumb-btn-container'}>
                  <Box className='actions-dropdown-container toolbar'>
                    <Button
                      id={'btn-county-links'}
                      disableTouchRipple
                      disableElevation
                      className='actions-button'
                      onClick={(event: React.MouseEvent<HTMLButtonElement>): void => setCountyLinkEl(event.currentTarget)}
                      startIcon={<LocationOnIcon />}
                      endIcon={Boolean(countyLinkEl) ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                      aria-controls={Boolean(countyLinkEl) ? 'actions-menu' : undefined}
                      aria-haspopup='true'
                      aria-expanded={Boolean(countyLinkEl) ? 'true' : undefined}>
                      {orderCounty?.name} County Links
                    </Button>
                    <Menu
                      id='menu-county-links'
                      anchorEl={countyLinkEl}
                      open={Boolean(countyLinkEl)}
                      onClose={(): void => setCountyLinkEl(null)}
                      className='actions-menu'
                      MenuListProps={{'aria-labelledby': 'actions-button'}}
                      anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
                      transformOrigin={{vertical: 'top', horizontal: 'left'}}>
                      {
                        countyLinks.length > 0
                          ? renderCountyLinks()
                          : <Typography id={'text-county-links-coming-soon'} className='county-link-not-found-text'>Coming Soon ...</Typography>
                      }
                    </Menu>
                  </Box>
                  <Button
                    id={'btn-refresh-title'}
                    className={'zitles-button stats-toolbar-button refresh'}
                    startIcon={<RefreshIcon />}
                    disableTouchRipple
                    onClick={refreshPage}>
                                      Refresh
                  </Button>
                  {
                    submitPermission &&
                      <Button id={'btn-submit-for-review'} className={'zitles-button stats-toolbar-button'} onClick={(): void => setOpenTitleDialog(true)}>Submit for Review</Button>
                  }
                  {
                    reviewPermission &&
                      <Button id={'btn-review-order'} className={'zitles-button stats-toolbar-button'} onClick={(): void => setOpenTitleDialog(true)}>Review Order</Button>
                  }
                  {
                    approvePermission &&
                      <Button id={'btn-approve-order'} className={'zitles-button stats-toolbar-button'} onClick={(): void => setOpenTitleDialog(true)}>Approve Order</Button>
                  }
                </Box>
              </Box>
              <Card className={'details-card'} elevation={0}>
                <CardContent>
                  <Box className={'details-card-header-container'}>
                    <Typography id={'text-details-header'} className={'details-card-header'}>Details</Typography>
                    {
                      editMode
                        ? submitLoading
                          ? <CircularProgress className={'zitles-icon'} size={14}/>
                          : <CheckCircle id={'btn-save-order'} className={'zitles-icon table-cell'} onClick={handleSubmitOrder}/>
                        : <EditIcon id={'btn-edit-order'} className={`zitles-icon ${!updatePermission ? 'disable' : ''} table-cell`} onClick={handleEditOrder}/>
                    }
                  </Box>
                  {
                    isExpanded &&
                      <Grid container spacing={2}>
                        <Grid size={{xs: 12, sm: 12, md: 12, lg: 6, xl: 6}}>
                          <Grid container className={'details-card-body primary'} spacing={2}>
                            {
                              orderDetails.map((data: KeyValueResource, index: number): JSX.Element =>
                                index === 0
                                  ? <Grid size={12} key={index}>
                                      <Box className={'details-card-item first'}>
                                        <Typography className={'details-card-item-label'}>{data.label}</Typography>
                                        <Typography className={'details-card-item-value'}>{data.value ?? <Skeleton/>}</Typography>
                                      </Box>
                                    </Grid>
                                  : <Grid size={6} key={index}>
                                      <Box className={`details-card-item ${editMode ? 'input-item' : ''}`}>
                                        <Typography className={`details-card-item-label ${editMode ? 'edit' : ''}`}>{data.label}</Typography>
                                        {renderTemplate(false, index, data.value)}
                                      </Box>
                                    </Grid>
                              )
                            }
                          </Grid>
                        </Grid>
                        <Grid size={{xs: 12, sm: 12, md: 12, lg: 6, xl: 6}}>
                          <Grid container className={'details-card-body'} spacing={2}>
                            {
                              assignDetails.map((data: KeyValueResource, index: number): JSX.Element =>
                                <Grid size={12} key={index}>
                                  <Box className={`details-card-item ${index === 0 ? 'first' : ''} ${editMode ? 'input-item' : ''}`}>
                                    <Typography className={`details-card-item-label ${editMode ? 'edit' : ''}`}>{data.label}</Typography>
                                    {renderTemplate(true, index, data.value)}
                                  </Box>
                                </Grid>
                              )
                            }
                          </Grid>
                        </Grid>
                      </Grid>
                  }
                </CardContent>
              </Card>
              <Box className={'title-details-toolbar'}>
                <Box display={'flex'} alignItems={'center'} gap={1}>
                  <ArrowBackIcon className={'zitles-icon disabled'}/>
                  <FormControl className={'parcel-dropdown'}>
                    <InputLabel sx={{zIndex: 0, fontSize: '125%'}}>Parcel Number</InputLabel>
                    <Select
                      id={'select-parcel-number'}
                      value={titleId}
                      label='Parcel Number'
                      onChange={handleParcelNumberChange}
                      sx={{fontSize: '125%'}}>
                      {
                        parcelNumberOptions.map((option: DropdownOption): JSX.Element => <MenuItem key={option.key} value={option.value}>{option.text}</MenuItem>)
                      }
                    </Select>
                  </FormControl>
                  <ArrowForwardIcon className={'zitles-icon disabled'}/>
                </Box>
                <Box className='actions-dropdown-container'>
                  <Button
                    id={'btn-actions-menu'}
                    disableTouchRipple
                    disableElevation
                    className='actions-button'
                    onClick={(event: React.MouseEvent<HTMLButtonElement>): void => setAnchorEl(event.currentTarget)}
                    startIcon={<AppsOutageIcon />}
                    endIcon={Boolean(anchorEl) ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                    aria-controls={Boolean(anchorEl) ? 'actions-menu' : undefined}
                    aria-haspopup='true'
                    aria-expanded={Boolean(anchorEl) ? 'true' : undefined}>
                    Actions
                  </Button>
                  <Menu
                    id='menu-actions'
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={(): void => setAnchorEl(null)}
                    className='actions-menu'
                    MenuListProps={{'aria-labelledby': 'actions-button'}}
                    anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
                    transformOrigin={{vertical: 'top', horizontal: 'left'}}>
                    {
                      actionList.map((pageOption: PageOption): JSX.Element => (
                        <MenuItem key={pageOption}>
                          <Button
                            id={`btn-action-${pageOption.toLowerCase().replace(/\s+/g, '-')}`}
                            className={`title-button ${isDisabled(pageOption) ? 'disable' : ''}`}
                            disableRipple
                            disabled={isDisabled(pageOption)}
                            onClick={(): void => handlePageOptionClick(pageOption)}>
                            { pageOption === PageOption.DOCUMENTS && <WorkIcon className={'title-button-icon'} /> }
                            { pageOption === PageOption.VALIDATE_DOCUMENTS && <CheckCircleOutlineIcon className={'title-button-icon'} /> }
                            { pageOption === PageOption.DOCUMENT_RUNNER && <SearchIcon className={'title-button-icon'} /> }
                            { pageOption === PageOption.EXPORT && <ImportExportIcon className={'title-button-icon'} /> }
                            { pageOption === PageOption.DOWNLOAD && <DownloadIcon className={'title-button-icon'} /> }
                            { pageOption === PageOption.IMPORT && <FileOpenIcon className={'title-button-icon'} /> }
                            { pageOption === PageOption.INVOICES && <ReceiptLongIcon className={'title-button-icon'} /> }
                            { pageOption === PageOption.REPORTS && <SummarizeIcon className={'title-button-icon'} /> }
                            { pageOption === PageOption.LINE_LIST_REPORTS && <ListAltIcon className={'title-button-icon'} /> }
                            { pageOption === PageOption.AUDIT_LOGS && <HistoryIcon className={'title-button-icon'} /> }
                            { pageOption }
                          </Button>
                        </MenuItem>
                      ))
                    }
                  </Menu>
                </Box>
              </Box>
              {
                !titleService.getCache()
                  ? <LoadingComponent/>
                  : <DragContainer
                      project={project}
                      strategy={rectSortingStrategy}
                      multiSelect
                      scrollable/>
              }
              {
                title &&
                  <CommentWidget orderNumber={project.orderNumber} title={title} currentUser={currentUser}/>
              }
            </>
          : <LoadingComponent/>
      }
      {
        openTitleDialog && title &&
          <TitleDialog
            open={openTitleDialog}
            setOpen={setOpenTitleDialog}
            title={title}/>
      }
      {
        <AuditLogsDialog
          open={openAuditLogsDialog}
          setOpen={setOpenAuditLogsDialog}
          organizationId={organizationId ?? ''}
          titleId={titleId ?? ''}
          titleStatus={title?.status}/>
      }
    </>
  );
}

export default observer(TitleDetails);
